import type { RecurringSeries } from "@/types/events.ts";

/**
 * One line that identifies a recurring meeting: how often, on what day, at what
 * time.
 *
 * The subject alone rarely settles it — "Sync" and "1:1" repeat across a
 * calendar — and the next occurrence alone reads like a one-off. Cadence is
 * what makes it a standing commitment the user recognises, which is what they
 * are being asked to decide about.
 *
 * Shared because web's settings screen, web's picker cards and native's
 * onboarding step all render this same row, and a meeting described three
 * slightly different ways is three chances to protect the wrong one.
 */
export function describeRecurrence(series: RecurringSeries): string {
  const cadence = cadenceLabel(series.typicalGapDays);
  const start = series.nextInstanceStartMs;
  if (!start) return cadence ?? "";

  const date = new Date(start);
  // A weekday is only a recurrence rule when the series meets about once a week
  // or less often. A daily standup does happen on Wednesday, but saying so
  // implies it doesn't happen on Thursday.
  const heading = cadence
    ? `${cadence}${isWeeklyOrRarer(series.typicalGapDays) ? ` on ${weekday(date)}` : ""}`
    : weekday(date);

  return [heading, formatTimeRange(start, series.nextInstanceEndMs)].filter(Boolean).join(" · ");
}

/**
 * How often, in words. Undefined for a payload carrying no gap, so the caller
 * falls back to what it can still say rather than asserting a cadence.
 *
 * "Every other week" rather than "biweekly", which English uses for both twice
 * a week and once a fortnight — and the user is deciding what they can give up.
 */
function cadenceLabel(typicalGapDays: number | undefined): string | undefined {
  if (typicalGapDays === undefined) return undefined;
  if (typicalGapDays <= 1.5) return "Daily";
  if (typicalGapDays < 5.5) return "A few times a week";
  if (typicalGapDays <= 9) return "Weekly";
  return "Every other week";
}

const isWeeklyOrRarer = (typicalGapDays: number | undefined) =>
  typicalGapDays !== undefined && typicalGapDays >= 5.5;

const weekday = (date: Date) => date.toLocaleDateString("en-US", { weekday: "short" });

/** "9:00 – 9:30 AM", collapsing the meridiem when both ends share one. */
function formatTimeRange(startMs: number, endMs: number | undefined): string {
  const start = new Date(startMs);
  if (!endMs) return formatTime(start);
  const end = new Date(endMs);
  const sharesMeridiem = start.getHours() < 12 === end.getHours() < 12;
  const startText = sharesMeridiem ? formatTime(start).replace(/\s(AM|PM)$/, "") : formatTime(start);
  return `${startText} – ${formatTime(end)}`;
}

const formatTime = (date: Date) =>
  date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
